// redux implementation
import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Plus, X, Copy, Check, Info } from 'lucide-react';
import Spinner from '../Spinner';
import styles from './PortfolioActionTable.module.sass';
import { getTopTraders, getAssetHolder } from '../../services/portfolio';
import { RootState } from '../../redux/store';
import TopTradersModal from 'components/Modal/TopTraders';
import { useAuth } from 'react-oidc-context';


const PortfolioActionTable = React.memo(() => {
  const auth = useAuth();
  const [activeTab, setActiveTab] = useState('Top Traders');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState('');
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const wallets = useSelector((state: RootState) => state.user.wallets);
  const selectedContract = useSelector((state: RootState) => state.user.selectedContract);
  const topTraders = useSelector((state: RootState) => state.portfolio.topTraders || {});
  const assetHolder = useSelector((state: RootState) => 
    selectedContract ? state.portfolio.assetHolder[selectedContract] || {} : {});
  const loading = useSelector((state: RootState) => state.portfolio.loading);

  const tabs = ['Top Traders', 'Holders', 'Whales'];

  const tradersArray = useMemo(() => {
    if (!selectedContract || !topTraders) return [];
    
    const tokenData = Object.values(topTraders).find(token => {
      if (!token || !token.contract_address) return false;
      return token.contract_address.toLowerCase() === selectedContract.toLowerCase();
    });
    
    return tokenData?.result || [];
  }, [selectedContract, topTraders]);
  
  const holdersArray = useMemo(() => {
    if (!assetHolder?.raw_holders?.owner_address) {
      return [];
    }
    
    return Object.keys(assetHolder.raw_holders.owner_address).map(address => ({
      owner_address: assetHolder.raw_holders.owner_address[address] || '',
      holder_group: assetHolder.raw_holders.holder_group?.[address] || '',
      is_contract: assetHolder.raw_holders.is_contract?.[address] || false,
      usd_value: assetHolder.raw_holders.usd_value?.[address] || '0',
      percentage_relative_to_total_supply: 
        assetHolder.raw_holders.percentage_relative_to_total_supply?.[address] || 0,
    }));
  }, [assetHolder]);

  useEffect(() => {
    const fetchData = async () => {
      if (!wallets.length || !selectedContract || !auth.user?.access_token) {
        return;
      }
      try {
        if (activeTab === 'Top Traders') {
          await getTopTraders(auth.user.access_token);
        } else {
          await getAssetHolder(auth.user.access_token);
        }
      } catch (error) {
        console.error('Error fetching data');
      }
    };
    fetchData();
  }, [wallets, selectedContract, activeTab, auth.user?.access_token]);

  const handleCopyAddress = (address: string) => {
    navigator.clipboard.writeText(address);
    setCopiedAddress(address);
    setTimeout(() => setCopiedAddress(null), 2000);
  };

  const truncateAddress = (address: string) => {
    if (!address) return '';
    return `${address.slice(0, 4)}...${address.slice(-4)}`;
  };

  const getDisplayData = () => {
    let data = activeTab === 'Top Traders' ? tradersArray : holdersArray;
    
    if (activeTab === 'Whales' && Array.isArray(data)) {
      data = data.filter(holder => {
        const percentageHeld = parseFloat(holder.percentage_relative_to_total_supply.toString());
        return !isNaN(percentageHeld) && percentageHeld > 1;
      });
    }
  
    return Array.isArray(data) ? data : [];
  };

  const paginatedData = () => {
    const data = getDisplayData();
    const startIndex = (page - 1) * itemsPerPage;
    return data.slice(startIndex, startIndex + itemsPerPage);
  };

  const totalPages = Math.ceil(getDisplayData().length / itemsPerPage);

  const formatNumber = (num: string | number, isPercent = false) => {
    if (!num) return '$0.00';
    const value = parseFloat(num.toString());
    if (isNaN(value)) return '$0.00';
    
    if (isPercent) {
      return `${value.toFixed(2)}%`;
    }
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(2)}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(2)}K`;
    }
    return `$${value.toFixed(2)}`;
  };

  const renderTableHeaders = () => {
    if (activeTab === 'Top Traders') {
      return (
        <tr>
          <th>Add</th>
          <th>Address</th>
          <th>Avg Buy</th>
          <th>Avg Sell</th>
          <th>Profit %</th>
          <th>$ Invested</th>
        </tr>
      );
    } else {
      return (
        <tr>
          <th>Add</th>
          <th>Owner Address</th>
          <th>USD Value</th>
          <th>% of Supply</th>
          <th>Type</th>
        </tr>
      );
    }
  };

  const renderTableRow = (item: any, index: number) => {
    if (activeTab === 'Top Traders') {
      return (
        <tr key={index}>
          <td>
            <button
              onClick={() => handleOpenModal(item.address)}
              className={styles.addButton}
              aria-label="Add to notification list"
            >
              <Plus size={14} />
            </button>
          </td>
          <td className={styles.addressCell}>
            <span>{truncateAddress(item.address)}</span>
            <button
              onClick={() => handleCopyAddress(item.address)}
              className={styles.copyButton}
              aria-label="Copy address"
            >
              {copiedAddress === item.address ? (
                <Check size={14} className={styles.checkIcon} />
              ) : (
                <Copy size={14} />
              )}
            </button>
          </td>
          <td>{formatNumber(item.avg_buy_price_usd)}</td>
          <td>{formatNumber(item.avg_sell_price_usd)}</td>
          <td className={parseFloat(item.realized_profit_percentage) >= 0 ? styles.positive : styles.negative}>
            {formatNumber(item.realized_profit_percentage, true)}
          </td>
          <td>{formatNumber(item.total_usd_invested)}</td>
        </tr>
      );
    } else {
      return (
        <tr key={index}>
          <td>
            <button
              onClick={() => handleOpenModal(item.owner_address)}
              className={styles.addButton}
              aria-label="Add to notification list"
            >
              <Plus size={14} />
            </button>
          </td>
          <td className={styles.addressCell}>
            <span>{truncateAddress(item.owner_address)}</span>
            <button
              onClick={() => handleCopyAddress(item.owner_address)}
              className={styles.copyButton}
              aria-label="Copy address"
            >
              {copiedAddress === item.owner_address ? (
                <Check size={16} className={styles.checkIcon} />
              ) : (
                <Copy size={16} />
              )}
            </button>
          </td>
          <td>{formatNumber(item.usd_value)}</td>
          <td>{formatNumber(item.percentage_relative_to_total_supply, true)}</td>
          <td>{item.holder_group || (item.is_contract ? 'Contract' : 'Individual')}</td>
        </tr>
      );
    }
  };

  const handleOpenModal = (asset: string) => {
    setSelectedAsset(asset);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedAsset('');
  };

  const handleOpenInfoModal = (tab: string) => {
    setActiveTab(tab);
    setIsInfoModalOpen(true);
  };

  if (!wallets.length) {
    return (
      <div className={styles.container}>
        <div className="text-center py-8">
          No wallets connected. Please add a wallet to view data.
        </div>
      </div>
    );
  }

  if (!selectedContract) {
    return (
      <div className={styles.container}>
        <div className="text-center py-8">
          <Spinner />
          {/* <p className="mt-4 text-gray-400">Loading contract data...</p> */}
        </div>
      </div>
    );
  }

  const isLoading = loading?.topTraders || loading?.assetHolder;

  return (
    <div className={styles.container}>
      <div className={styles.topControls}>
        <div className={styles.actionButtons}>
          <div className={styles.tabButtons}>
            {tabs.map((tab) => (
              <div key={tab} className={styles.tabGroup}>
                <button
                  onClick={() => setActiveTab(tab)}
                  className={`${styles.actionButton} ${
                    activeTab === tab ? styles.active : ''
                  }`}
                  aria-pressed={activeTab === tab}
                >
                  {tab}
                </button>
                <button 
                  onClick={() => handleOpenInfoModal(tab)}
                  className={styles.infoButton}
                  aria-label={`${tab} information`}
                >
                  <Info size={14} />
                </button>
              </div>
            ))}
          </div>
          
          <div className={styles.headerPagination}>
            {!isLoading && totalPages > 0 && (
              <>
                <button
                  onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                  disabled={page === 1}
                  className={styles.paginationButton}
                  aria-label="Previous page"
                >
                  Prev
                </button>
                <span className={styles.pageInfo}>
                  {page}/{totalPages}
                </span>
                <button
                  onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={page === totalPages}
                  className={styles.paginationButton}
                  aria-label="Next page"
                >
                  Next
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            {renderTableHeaders()}
          </thead>
          <tbody>
            {!isLoading ? (
              paginatedData().length > 0 ? (
                paginatedData().map((item, index) => renderTableRow(item, index))
              ) : (
                <tr>
                  <td colSpan={activeTab === 'Top Traders' ? 7 : 5} className="text-center py-6 text-gray-400">
                    No data available for this selection.
                  </td>
                </tr>
              )
            ) : (
              <tr>
                <td colSpan={activeTab === 'Top Traders' ? 6 : 5} className="text-center py-6">
                  <Spinner />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Subscription Modal */}
      {isModalOpen && (
        <div className={styles.modalOverlay} role="dialog" aria-modal="true">
          <div className={styles.modal}>
            <button 
              onClick={handleCloseModal} 
              className={styles.closeButton}
              aria-label="Close modal"
            >
              <X size={20} />
            </button>
            <h2>Add Wallet to Notifications</h2>
            <p>You're about to add {truncateAddress(selectedAsset)} to your notification list. Support for real-time wallet activity notifications is coming soon.</p>
          </div>
        </div>
      )}

      {/* Full Screen Info Modal */}
      {isInfoModalOpen && activeTab === 'Top Traders' && (
        <TopTradersModal
          tradersArray={tradersArray}
          onClose={() => setIsInfoModalOpen(false)}
        />
      )}
      
      {/* Holders modal */}
      {isInfoModalOpen && activeTab === 'Holders' && (
        <div className={styles.fullScreenModal} role="dialog" aria-modal="true">
          <h2>Holder Analysis</h2>
          <button 
            onClick={() => setIsInfoModalOpen(false)} 
            className={styles.closeButton}
            aria-label="Close modal"
          >
            <X size={24} />
          </button>
          <div className={styles.modalContent}>
            {/* Holders content */}
            <p>Detailed holder analysis coming soon.</p>
          </div>
        </div>
      )}
      
      {/* Whales modal */}
      {isInfoModalOpen && activeTab === 'Whales' && (
        <div className={styles.fullScreenModal} role="dialog" aria-modal="true">
          <h2>Whale Analysis</h2>
          <button 
            onClick={() => setIsInfoModalOpen(false)} 
            className={styles.closeButton}
            aria-label="Close modal"
          >
            <X size={24} />
          </button>
          <div className={styles.modalContent}>
            {/* Whales content */}
            <p>Detailed whale analysis coming soon.</p>
          </div>
        </div>
      )}
    </div>
  );
});

export default PortfolioActionTable;
